import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        // Hash-flow (implicit / recovery / magic link with #access_token=...)
        const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
        const hashParams = new URLSearchParams(hash);
        const errDesc = url.searchParams.get("error_description") ?? hashParams.get("error_description");
        if (errDesc) throw new Error(errDesc);

        // PKCE-flow: ?code=...
        const code = url.searchParams.get("code");
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (hashParams.get("access_token") && hashParams.get("refresh_token")) {
          // Implicit-flow fallback
          const { error } = await supabase.auth.setSession({
            access_token: hashParams.get("access_token")!,
            refresh_token: hashParams.get("refresh_token")!,
          });
          if (error) throw error;
        }
        // Ellers: Supabase har allerede satt sesjon via verify-endepunktet.

        const { data } = await supabase.auth.getSession();
        const type = url.searchParams.get("type") ?? hashParams.get("type");
        if (type === "recovery") {
          navigate({ to: "/reset-password", replace: true });
          return;
        }
        navigate({ to: data.session ? "/dashboard" : "/auth", replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Ukjent feil ved innlogging");
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-surface p-6">
      <div className="card p-8 text-center max-w-sm">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-destructive">Kunne ikke fullføre innlogging</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <button
              className="btn btn-primary mt-6"
              onClick={() => navigate({ to: "/auth", replace: true })}
            >
              Til innlogging
            </button>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
            <p className="mt-3 text-sm text-muted-foreground">Bekrefter innlogging …</p>
          </>
        )}
      </div>
    </div>
  );
}
